import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import ComplianceItemForm from '../components/ComplianceItemForm';
import CompleteComplianceDialog from '../components/CompleteComplianceDialog';
import ComplianceDetail from '../components/ComplianceDetail';
import ComplianceCalendar from '../components/ComplianceCalendar';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  ShieldCheck,
  AlertTriangle,
  Clock,
  CalendarDays,
  CheckCircle2,
  Plus,
  MoreVertical,
  Eye,
  Edit2,
  Check,
  MapPin,
  User,
  Tag,
  Loader2,
  RefreshCw,
  X,
  Trophy,
  TrendingUp,
  TrendingDown,
  Minus,
  Medal,
  BarChart3,
} from 'lucide-react';

const COMPLIANCE_TYPES = [
  { value: 'keuring', label: 'Keuring' },
  { value: 'contract', label: 'Contract' },
  { value: 'onderhoud', label: 'Onderhoud' },
  { value: 'voertuig', label: 'Voertuig' },
  { value: 'vergunning', label: 'Vergunning' },
  { value: 'betaling', label: 'Betaling' },
];

const STATUS_CONFIG = {
  overdue: {
    label: 'Te laat',
    badgeClass: 'bg-red-50 text-red-700 border-red-200',
    dotClass: 'bg-red-500',
  },
  due_soon: {
    label: 'Binnenkort',
    badgeClass: 'bg-orange-50 text-orange-700 border-orange-200',
    dotClass: 'bg-orange-500',
  },
  upcoming: {
    label: 'Aankomend',
    badgeClass: 'bg-blue-50 text-blue-700 border-blue-200',
    dotClass: 'bg-[#2E75B6]',
  },
  completed: {
    label: 'Afgerond',
    badgeClass: 'bg-green-50 text-green-700 border-green-200',
    dotClass: 'bg-green-600',
  },
};

const TYPE_CONFIG = {
  keuring: { label: 'Keuring', color: 'bg-purple-50 text-purple-700 border-purple-200' },
  contract: { label: 'Contract', color: 'bg-blue-50 text-blue-700 border-blue-200' },
  onderhoud: { label: 'Onderhoud', color: 'bg-amber-50 text-amber-700 border-amber-200' },
  voertuig: { label: 'Voertuig', color: 'bg-teal-50 text-teal-700 border-teal-200' },
  vergunning: { label: 'Vergunning', color: 'bg-rose-50 text-rose-700 border-rose-200' },
  betaling: { label: 'Betaling', color: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
};

function formatDate(dateStr) {
  if (!dateStr) return '';
  try {
    return new Date(dateStr).toLocaleDateString('nl-NL', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  } catch {
    return dateStr;
  }
}

function daysUntil(dateStr) {
  if (!dateStr) return null;
  const now = new Date();
  const due = new Date(dateStr);
  return Math.ceil((due - now) / (1000 * 60 * 60 * 24));
}

export default function ComplianceDashboard() {
  const { authAxios } = useAuth();
  const [stats, setStats] = useState(null);
  const [items, setItems] = useState([]);
  const [locations, setLocations] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  // Filters
  const [filterLocation, setFilterLocation] = useState('all');
  const [filterType, setFilterType] = useState('all');
  const [filterAssigned, setFilterAssigned] = useState('all');
  const [rangeDays, setRangeDays] = useState('30');

  // Dialogs
  const [formOpen, setFormOpen] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [completeItem, setCompleteItem] = useState(null);
  const [detailItem, setDetailItem] = useState(null);

  // View mode: 'timeline' or 'scores'
  const [viewMode, setViewMode] = useState('timeline');
  const [scores, setScores] = useState([]);
  const [scoresLoading, setScoresLoading] = useState(false);

  // Bulk selection
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkDialogOpen, setBulkDialogOpen] = useState(false);
  const [bulkNotes, setBulkNotes] = useState('');
  const [bulkCompleting, setBulkCompleting] = useState(false);

  const fetchData = useCallback(async (showRefresh = false) => {
    if (showRefresh) setRefreshing(true);
    try {
      const api = authAxios();
      const params = new URLSearchParams();
      if (filterLocation !== 'all') params.append('location_id', filterLocation);
      if (filterType !== 'all') params.append('type', filterType);
      if (filterAssigned !== 'all') params.append('assigned_to', filterAssigned);
      params.append('range_days', rangeDays);
      params.append('include_completed', 'false');
      params.append('limit', '200');

      const statsParams = new URLSearchParams();
      if (filterLocation !== 'all') statsParams.append('location_id', filterLocation);

      const [statsRes, itemsRes, locRes, usersRes] = await Promise.all([
        api.get(`/compliance-items/stats?${statsParams}`),
        api.get(`/compliance-items?${params}`),
        api.get('/locations'),
        api.get('/users/list'),
      ]);

      setStats(statsRes.data);
      setItems(itemsRes.data.items || []);
      setLocations(locRes.data);
      setUsers(usersRes.data);
    } catch (e) {
      console.error('Failed to load compliance data:', e);
      toast.error('Fout bij laden compliance data');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [authAxios, filterLocation, filterType, filterAssigned, rangeDays]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const fetchScores = useCallback(async () => {
    setScoresLoading(true);
    try {
      const api = authAxios();
      const res = await api.get('/compliance-scores');
      setScores(res.data.scores || []);
    } catch (e) {
      console.error('Failed to load scores:', e);
    } finally {
      setScoresLoading(false);
    }
  }, [authAxios]);

  useEffect(() => {
    if (viewMode === 'scores') fetchScores();
  }, [viewMode, fetchScores]);

  const handleCreated = () => {
    setFormOpen(false);
    setEditItem(null);
    fetchData(true);
  };

  const handleCompleted = () => {
    setCompleteItem(null);
    fetchData(true);
  };

  const handleEdit = (item) => {
    setEditItem(item);
    setFormOpen(true);
  };

  const handleDelete = async (item) => {
    if (!window.confirm(`Weet je zeker dat je "${item.title}" wilt verwijderen?`)) return;
    try {
      const api = authAxios();
      await api.delete(`/compliance-items/${item.compliance_id}`);
      toast.success('Compliance item verwijderd');
      fetchData(true);
    } catch (e) {
      toast.error('Fout bij verwijderen');
    }
  };

  const toggleSelect = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const selectAllOverdue = () => {
    setSelectedIds(new Set(overdueItems.map(i => i.compliance_id)));
  };

  const clearSelection = () => setSelectedIds(new Set());

  const handleBulkComplete = async () => {
    setBulkCompleting(true);
    try {
      const api = authAxios();
      const res = await api.post('/compliance-items/bulk-complete', {
        item_ids: Array.from(selectedIds),
        notes: bulkNotes,
      });
      toast.success(`${res.data.completed} items afgerond`);
      setBulkDialogOpen(false);
      setBulkNotes('');
      clearSelection();
      fetchData(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Fout bij bulk afronden');
    } finally {
      setBulkCompleting(false);
    }
  };

  const overdueItems = useMemo(() => items.filter(i => i.status === 'overdue'), [items]);
  const timelineItems = useMemo(() => items.filter(i => i.status !== 'overdue'), [items]);

  const locationMap = useMemo(() => {
    const map = {};
    locations.forEach(l => { map[l.location_id] = l.name; });
    return map;
  }, [locations]);

  const userMap = useMemo(() => {
    const map = {};
    users.forEach(u => { map[u.email] = u.name; });
    return map;
  }, [users]);

  const clearFilters = () => {
    setFilterLocation('all');
    setFilterType('all');
    setFilterAssigned('all');
  };

  const hasActiveFilters = filterLocation !== 'all' || filterType !== 'all' || filterAssigned !== 'all';

  if (loading) {
    return (
      <div className="space-y-6 max-w-[1400px]">
        <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-3">
          {[1,2,3,4,5].map(i => (
            <div key={i} className="bg-white rounded-xl border border-slate-200/80 p-4">
              <Skeleton className="h-3 w-20 mb-3" />
              <Skeleton className="h-8 w-16" />
            </div>
          ))}
        </div>
        <div className="bg-white rounded-xl border border-slate-200/80 p-4">
          <Skeleton className="h-8 w-full mb-3" />
          <Skeleton className="h-8 w-full mb-3" />
          <Skeleton className="h-8 w-full" />
        </div>
      </div>
    );
  }

  return (
    <TooltipProvider>
      <div className="space-y-4 sm:space-y-6 max-w-[1400px]" data-testid="compliance-dashboard">
        {/* KPI Strip */}
        <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-3" data-testid="compliance-kpi-strip">
          <KpiCard
            icon={ShieldCheck}
            label="Totaal"
            value={stats?.total || 0}
            color="#2E75B6"
            testId="compliance-kpi-total"
          />
          <KpiCard
            icon={AlertTriangle}
            label="Te laat"
            value={stats?.overdue || 0}
            color="#DC2626"
            highlight={stats?.overdue > 0}
            testId="compliance-kpi-overdue"
          />
          <KpiCard
            icon={Clock}
            label="Binnen 7 dagen"
            value={stats?.due_7_days || 0}
            color="#EA580C"
            testId="compliance-kpi-due-7-days"
          />
          <KpiCard
            icon={CalendarDays}
            label="Binnen 30 dagen"
            value={stats?.due_30_days || 0}
            color="#CA8A04"
            testId="compliance-kpi-due-30-days"
          />
          <KpiCard
            icon={CheckCircle2}
            label="Afgerond deze maand"
            value={stats?.completed_this_month || 0}
            color="#16A34A"
            testId="compliance-kpi-completed-this-month"
          />
        </div>

        {/* View Toggle: Timeline vs Scores */}
        <div className="flex items-center gap-3" data-testid="compliance-view-toggle">
          <Tabs value={viewMode} onValueChange={setViewMode}>
            <TabsList className="h-9">
              <TabsTrigger value="timeline" className="text-xs px-4 gap-1.5" data-testid="compliance-tab-timeline">
                <CalendarDays className="w-3.5 h-3.5" /> Timeline
              </TabsTrigger>
              <TabsTrigger value="scores" className="text-xs px-4 gap-1.5" data-testid="compliance-tab-scores">
                <Trophy className="w-3.5 h-3.5" /> Scores
              </TabsTrigger>
            </TabsList>
          </Tabs>
        </div>

        {viewMode === 'scores' ? (
          <ScoresLeaderboard scores={scores} loading={scoresLoading} onRefresh={fetchScores} />
        ) : (
        <>
        {/* Filter Bar + Actions */}
        <div className="bg-white dark:bg-[#161B22] rounded-xl border border-slate-200/80 dark:border-[#21262D] p-3 sm:p-4" data-testid="compliance-filter-bar">
          <div className="flex flex-col lg:flex-row lg:items-end gap-3">
            {/* Filters row */}
            <div className="flex-1 grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div>
                <label className="text-[10px] uppercase tracking-wider text-slate-500 font-medium mb-1 block">Locatie</label>
                <Select value={filterLocation} onValueChange={setFilterLocation}>
                  <SelectTrigger data-testid="compliance-filter-location" className="h-10 sm:h-9 text-sm">
                    <SelectValue placeholder="Alle locaties" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Alle locaties</SelectItem>
                    {locations.map(loc => (
                      <SelectItem key={loc.location_id} value={loc.location_id}>{loc.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wider text-slate-500 font-medium mb-1 block">Type</label>
                <Select value={filterType} onValueChange={setFilterType}>
                  <SelectTrigger data-testid="compliance-filter-type" className="h-10 sm:h-9 text-sm">
                    <SelectValue placeholder="Alle types" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Alle types</SelectItem>
                    {COMPLIANCE_TYPES.map(t => (
                      <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wider text-slate-500 font-medium mb-1 block">Toegewezen aan</label>
                <Select value={filterAssigned} onValueChange={setFilterAssigned}>
                  <SelectTrigger data-testid="compliance-filter-assigned" className="h-10 sm:h-9 text-sm">
                    <SelectValue placeholder="Iedereen" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Iedereen</SelectItem>
                    {users.map(u => (
                      <SelectItem key={u.email} value={u.email}>{u.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {hasActiveFilters && (
                <button
                  onClick={clearFilters}
                  className="self-end h-9 px-2 text-xs text-slate-500 hover:text-slate-700 flex items-center gap-1 transition-colors duration-150"
                  data-testid="compliance-clear-filters"
                >
                  <X className="w-3 h-3" /> Wis filters
                </button>
              )}
            </div>

            {/* Timeline toggle + CTA */}
            <div className="flex items-end gap-3">
              <Tabs value={rangeDays} onValueChange={setRangeDays} data-testid="compliance-timeline-range-tabs">
                <TabsList className="h-9">
                  <TabsTrigger value="7" className="text-xs px-3">7d</TabsTrigger>
                  <TabsTrigger value="30" className="text-xs px-3">30d</TabsTrigger>
                  <TabsTrigger value="90" className="text-xs px-3">90d</TabsTrigger>
                  <TabsTrigger value="180" className="text-xs px-3">180d</TabsTrigger>
                  <TabsTrigger value="365" className="text-xs px-3">1 jaar</TabsTrigger>
                  <TabsTrigger value="0" className="text-xs px-3">Alles</TabsTrigger>
                </TabsList>
              </Tabs>
              <Button
                onClick={() => { setEditItem(null); setFormOpen(true); }}
                data-testid="compliance-create-item-button"
                className="h-9 bg-[#2E75B6] hover:bg-[#27649C] text-white gap-1.5"
              >
                <Plus className="w-4 h-4" />
                <span className="hidden sm:inline">Nieuw item</span>
              </Button>
              <Button
                variant="outline"
                size="icon"
                onClick={() => fetchData(true)}
                disabled={refreshing}
                className="h-9 w-9"
                data-testid="compliance-refresh-button"
              >
                <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
              </Button>
            </div>
          </div>
        </div>

        {/* Main content: Timeline + Calendar */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 sm:gap-6">
          {/* Timeline column */}
          <div className="lg:col-span-8 space-y-4">
            {/* Overdue pinned section */}
            {overdueItems.length > 0 && (
              <div className="bg-white dark:bg-[#161B22] rounded-xl border border-slate-200/80 dark:border-[#21262D] overflow-hidden" data-testid="compliance-overdue-section">
                <div className="border-l-4 border-red-500">
                  <div className="px-4 py-3 bg-red-50/50 dark:bg-red-500/5 border-b border-red-100 dark:border-red-500/20 flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4 text-red-600 dark:text-red-400" />
                    <span className="text-sm font-semibold text-red-700 dark:text-red-400 font-heading">Te laat</span>
                    <Badge variant="outline" className="ml-auto bg-red-100 dark:bg-red-500/20 text-red-700 dark:text-red-400 border-red-200 dark:border-red-500/30 text-xs tabular-nums">
                      {overdueItems.length}
                    </Badge>
                    <button
                      onClick={selectAllOverdue}
                      className="text-[10px] font-medium text-red-600 dark:text-red-400 hover:underline ml-2"
                      data-testid="compliance-select-all-overdue"
                    >
                      Selecteer alle
                    </button>
                  </div>
                  <div className="divide-y divide-slate-100 dark:divide-[#21262D]">
                    {overdueItems.map(item => (
                      <ComplianceRow
                        key={item.compliance_id}
                        item={item}
                        locationMap={locationMap}
                        userMap={userMap}
                        onView={setDetailItem}
                        onEdit={handleEdit}
                        onComplete={setCompleteItem}
                        onDelete={handleDelete}
                        selected={selectedIds.has(item.compliance_id)}
                        onToggleSelect={toggleSelect}
                      />
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Timeline list */}
            <div className="bg-white rounded-xl border border-slate-200/80 overflow-hidden" data-testid="compliance-timeline-list">
              <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
                <span className="text-sm font-semibold text-slate-900 font-heading">
                  {rangeDays === '0' ? 'Alle toekomstige items' : `Komende ${rangeDays} dagen`}
                </span>
                <span className="text-xs text-slate-400 tabular-nums">
                  {timelineItems.length} items
                </span>
              </div>
              {timelineItems.length === 0 && overdueItems.length === 0 ? (
                <div className="px-4 py-12 text-center">
                  <ShieldCheck className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                  <p className="text-sm text-slate-500" data-testid="compliance-empty-state">
                    Geen compliance-items gevonden voor de gekozen filters.
                  </p>
                  <Button
                    variant="outline"
                    className="mt-4"
                    onClick={() => { setEditItem(null); setFormOpen(true); }}
                  >
                    <Plus className="w-4 h-4 mr-1" /> Eerste item aanmaken
                  </Button>
                </div>
              ) : timelineItems.length === 0 ? (
                <div className="px-4 py-8 text-center">
                  <p className="text-sm text-slate-400">
                    Geen aankomende items{rangeDays !== '0' ? ` in de komende ${rangeDays} dagen` : ''}.
                  </p>
                </div>
              ) : (
                <div className="divide-y divide-slate-100 dark:divide-[#21262D]">
                  {timelineItems.map(item => (
                    <ComplianceRow
                      key={item.compliance_id}
                      item={item}
                      locationMap={locationMap}
                      userMap={userMap}
                      onView={setDetailItem}
                      onEdit={handleEdit}
                      onComplete={setCompleteItem}
                      onDelete={handleDelete}
                      selected={selectedIds.has(item.compliance_id)}
                      onToggleSelect={toggleSelect}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Calendar column */}
          <div className="lg:col-span-4">
            <ComplianceCalendar items={[...overdueItems, ...timelineItems]} />
          </div>
        </div>

        {/* Dialogs */}
        </>
        )}

        {/* Bulk Selection Floating Bar */}
        {selectedIds.size > 0 && (
          <div className="fixed bottom-4 sm:bottom-6 left-2 right-2 sm:left-1/2 sm:right-auto sm:-translate-x-1/2 sm:w-auto z-50 bg-slate-900 dark:bg-[#21262D] text-white rounded-xl shadow-2xl border border-slate-700 dark:border-[#30363D] px-4 sm:px-6 py-3 flex items-center justify-between sm:justify-start gap-3 sm:gap-4" data-testid="bulk-action-bar">
            <span className="text-sm font-medium tabular-nums">{selectedIds.size} geselecteerd</span>
            <div className="w-px h-6 bg-slate-600" />
            <button
              onClick={() => setBulkDialogOpen(true)}
              className="flex items-center gap-1.5 px-4 py-1.5 rounded-lg bg-green-600 hover:bg-green-700 text-white text-sm font-medium transition-colors"
              data-testid="bulk-complete-button"
            >
              <Check className="w-4 h-4" /> Afvinken
            </button>
            <button
              onClick={clearSelection}
              className="text-sm text-slate-400 hover:text-white transition-colors"
              data-testid="bulk-cancel-button"
            >
              Annuleren
            </button>
          </div>
        )}

        {/* Bulk Complete Dialog */}
        {bulkDialogOpen && (
          <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/50" data-testid="bulk-complete-dialog">
            <div className="bg-white dark:bg-[#161B22] rounded-t-xl sm:rounded-xl border border-slate-200 dark:border-[#21262D] shadow-2xl w-full sm:max-w-md sm:mx-4 p-5 sm:p-6">
              <h3 className="font-heading text-base font-semibold text-slate-900 dark:text-[#E6EDF3] mb-1">
                {selectedIds.size} items afronden
              </h3>
              <p className="text-xs text-slate-500 dark:text-[#8B949E] mb-4">
                Alle geselecteerde items worden als afgerond gemarkeerd.
              </p>
              <div className="mb-4">
                <label className="text-xs font-medium text-slate-600 dark:text-[#8B949E] block mb-1.5">Notitie (optioneel)</label>
                <textarea
                  value={bulkNotes}
                  onChange={(e) => setBulkNotes(e.target.value)}
                  className="w-full h-20 rounded-lg border border-slate-200 dark:border-[#30363D] bg-white dark:bg-[#0D1117] text-sm text-slate-900 dark:text-[#E6EDF3] px-3 py-2 resize-none focus:ring-2 focus:ring-[#2E75B6] focus:border-transparent"
                  placeholder="Bijv. 'Maandelijkse controle uitgevoerd'"
                  data-testid="bulk-complete-notes"
                />
              </div>
              <div className="flex items-center justify-end gap-2">
                <button
                  onClick={() => { setBulkDialogOpen(false); setBulkNotes(''); }}
                  className="px-4 py-2 rounded-lg text-sm text-slate-600 dark:text-[#8B949E] hover:bg-slate-100 dark:hover:bg-[#21262D] transition-colors"
                >
                  Annuleren
                </button>
                <button
                  onClick={handleBulkComplete}
                  disabled={bulkCompleting}
                  className="flex items-center gap-1.5 px-5 py-2 rounded-lg bg-green-600 text-white text-sm font-medium hover:bg-green-700 transition-colors disabled:opacity-50"
                  data-testid="bulk-complete-confirm"
                >
                  {bulkCompleting ? (
                    <><Loader2 className="w-4 h-4 animate-spin" /> Afronden...</>
                  ) : (
                    <><Check className="w-4 h-4" /> Alles afvinken</>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}
        <ComplianceItemForm
          open={formOpen}
          onOpenChange={(open) => { if (!open) { setFormOpen(false); setEditItem(null); } }}
          item={editItem}
          locations={locations}
          users={users}
          onSuccess={handleCreated}
        />

        {completeItem && (
          <CompleteComplianceDialog
            open={!!completeItem}
            onOpenChange={(open) => { if (!open) setCompleteItem(null); }}
            item={completeItem}
            onSuccess={handleCompleted}
          />
        )}

        {detailItem && (
          <ComplianceDetail
            open={!!detailItem}
            onOpenChange={(open) => { if (!open) setDetailItem(null); }}
            item={detailItem}
            locationMap={locationMap}
            userMap={userMap}
            onEdit={(item) => { setDetailItem(null); handleEdit(item); }}
            onComplete={(item) => { setDetailItem(null); setCompleteItem(item); }}
          />
        )}
      </div>
    </TooltipProvider>
  );
}

// KPI Card
function KpiCard({ icon: Icon, label, value, color, highlight, testId }) {
  return (
    <div
      className={`bg-white rounded-xl border border-slate-200/80 px-4 py-3 card-hover ${highlight ? 'ring-1 ring-red-200' : ''}`}
      data-testid={testId}
    >
      <div className="flex items-center gap-2 mb-1.5">
        <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${color}22` }}>
          <Icon className="w-3.5 h-3.5" style={{ color }} />
        </div>
        <span className="text-xs font-medium uppercase tracking-wide text-slate-600">{label}</span>
      </div>
      <p className="text-2xl md:text-3xl font-heading font-semibold text-slate-900 tabular-nums">{value}</p>
    </div>
  );
}

// Compliance Row
function ComplianceRow({ item, locationMap, userMap, onView, onEdit, onComplete, onDelete, selected, onToggleSelect }) {
  const statusCfg = STATUS_CONFIG[item.status] || STATUS_CONFIG.upcoming;
  const typeCfg = TYPE_CONFIG[item.type] || { label: item.type, color: 'bg-slate-50 text-slate-700 border-slate-200' };
  const days = daysUntil(item.due_date);

  let daysLabel = '';
  if (item.status === 'overdue' && days !== null) {
    daysLabel = `${Math.abs(days)} dagen te laat`;
  } else if (days !== null && days >= 0) {
    daysLabel = days === 0 ? 'Vandaag' : days === 1 ? 'Morgen' : `Nog ${days} dagen`;
  }

  return (
    <div
      className={`flex items-start justify-between gap-3 px-4 py-3 hover:bg-slate-50/70 dark:hover:bg-[#1C2128] transition-colors duration-150 cursor-pointer group ${selected ? 'bg-blue-50/50 dark:bg-blue-500/5' : ''}`}
      data-testid="compliance-item-row"
      onClick={() => onView(item)}
    >
      {/* Checkbox */}
      <div className="flex-shrink-0 pt-0.5" onClick={(e) => e.stopPropagation()}>
        <input
          type="checkbox"
          checked={selected || false}
          onChange={() => onToggleSelect(item.compliance_id)}
          className="w-4 h-4 rounded border-slate-300 dark:border-[#30363D] text-[#2E75B6] focus:ring-[#2E75B6] cursor-pointer"
          data-testid={`compliance-checkbox-${item.compliance_id}`}
        />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1">
          <span className="text-sm font-medium text-slate-900 truncate" data-testid="compliance-item-title">
            {item.title}
          </span>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <span className={`text-[10px] px-1.5 py-0.5 rounded border font-medium ${typeCfg.color}`}>
            {typeCfg.label}
          </span>
          <span className="text-xs text-slate-500 flex items-center gap-1">
            <MapPin className="w-3 h-3" />
            {locationMap[item.location_id] || item.location_id}
          </span>
          <span className="text-xs text-slate-500 flex items-center gap-1">
            <User className="w-3 h-3" />
            {userMap[item.assigned_to] || item.assigned_to}
          </span>
        </div>
      </div>
      <div className="flex items-center gap-2 flex-shrink-0">
        <div className="text-right">
          <p className="text-sm font-medium tabular-nums text-slate-900">
            {formatDate(item.due_date)}
          </p>
          {daysLabel && (
            <p className={`text-[10px] ${item.status === 'overdue' ? 'text-red-600 font-medium' : 'text-slate-500'}`}>
              {daysLabel}
            </p>
          )}
        </div>
        <Badge variant="outline" className={`text-[10px] px-1.5 py-0.5 ${statusCfg.badgeClass}`}>
          {statusCfg.label}
        </Badge>
        {/* Quick-complete button */}
        {item.status !== 'completed' && (
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                onClick={(e) => { e.stopPropagation(); onComplete(item); }}
                className="p-1.5 rounded-md text-green-600 hover:bg-green-50 dark:hover:bg-green-500/10 transition-colors duration-150"
                data-testid={`quick-complete-${item.compliance_id}`}
                aria-label="Snel afronden"
              >
                <Check className="w-4 h-4" />
              </button>
            </TooltipTrigger>
            <TooltipContent side="top"><p className="text-xs">Snel afronden</p></TooltipContent>
          </Tooltip>
        )}
        <DropdownMenu>
          <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
            <button
              className="p-1.5 rounded-md text-slate-400 opacity-0 group-hover:opacity-100 hover:text-slate-600 hover:bg-slate-100 transition-opacity duration-150"
              data-testid="compliance-item-actions-menu"
              aria-label="Acties"
            >
              <MoreVertical className="w-4 h-4" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem
              onClick={(e) => { e.stopPropagation(); onView(item); }}
              data-testid="compliance-item-open"
            >
              <Eye className="w-4 h-4 mr-2" /> Bekijken
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={(e) => { e.stopPropagation(); onEdit(item); }}
            >
              <Edit2 className="w-4 h-4 mr-2" /> Bewerken
            </DropdownMenuItem>
            {item.status !== 'completed' && (
              <DropdownMenuItem
                onClick={(e) => { e.stopPropagation(); onComplete(item); }}
                data-testid="compliance-item-mark-complete"
              >
                <Check className="w-4 h-4 mr-2" /> Afronden
              </DropdownMenuItem>
            )}
            <DropdownMenuItem
              onClick={(e) => { e.stopPropagation(); onDelete(item); }}
              className="text-red-600 focus:text-red-600"
            >
              <X className="w-4 h-4 mr-2" /> Verwijderen
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  );
}


// Scores Leaderboard
function ScoresLeaderboard({ scores, loading, onRefresh }) {
  if (loading) {
    return (
      <div className="bg-white dark:bg-[#161B22] rounded-xl border border-slate-200/80 dark:border-[#21262D] p-8 text-center">
        <Loader2 className="w-6 h-6 animate-spin text-[#2E75B6] mx-auto mb-2" />
        <p className="text-sm text-slate-500">Scores berekenen...</p>
      </div>
    );
  }

  const scoredLocations = scores.filter(s => s.score != null);
  const noDataLocations = scores.filter(s => s.score == null);
  const top3 = scoredLocations.slice(0, 3);

  const getScoreColor = (score) => {
    if (score >= 90) return { text: 'text-green-700 dark:text-green-400', bg: 'bg-green-50 dark:bg-green-500/10', border: 'border-green-200 dark:border-green-500/30', hex: '#16A34A' };
    if (score >= 70) return { text: 'text-[#2E75B6] dark:text-blue-400', bg: 'bg-blue-50 dark:bg-blue-500/10', border: 'border-blue-200 dark:border-blue-500/30', hex: '#2E75B6' };
    if (score >= 50) return { text: 'text-orange-700 dark:text-orange-400', bg: 'bg-orange-50 dark:bg-orange-500/10', border: 'border-orange-200 dark:border-orange-500/30', hex: '#EA580C' };
    return { text: 'text-red-700 dark:text-red-400', bg: 'bg-red-50 dark:bg-red-500/10', border: 'border-red-200 dark:border-red-500/30', hex: '#DC2626' };
  };

  const getLabel = (score) => {
    if (score >= 90) return 'Uitstekend';
    if (score >= 70) return 'Goed';
    if (score >= 50) return 'Aandacht nodig';
    return 'Kritiek';
  };

  const podiumOrder = top3.length === 3 ? [top3[1], top3[0], top3[2]] : top3;
  const podiumHeights = top3.length === 3 ? ['h-20', 'h-28', 'h-16'] : top3.length === 2 ? ['h-24', 'h-20'] : ['h-28'];
  const podiumLabels = top3.length === 3 ? ['2', '1', '3'] : top3.length === 2 ? ['1', '2'] : ['1'];

  return (
    <div className="space-y-4" data-testid="compliance-scores-leaderboard">
      {/* Podium */}
      {top3.length >= 2 && (
        <div className="bg-white dark:bg-[#161B22] rounded-xl border border-slate-200/80 dark:border-[#21262D] p-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-2">
              <Trophy className="w-5 h-5 text-amber-500" />
              <h3 className="font-heading text-base font-semibold text-slate-900 dark:text-[#E6EDF3]">Compliance Leaderboard</h3>
            </div>
            <Button variant="outline" size="sm" onClick={onRefresh} className="h-8 text-xs gap-1.5" data-testid="scores-refresh-button">
              <RefreshCw className="w-3.5 h-3.5" /> Ververs
            </Button>
          </div>

          <div className="flex items-end justify-center gap-4 mb-4">
            {podiumOrder.map((loc, i) => {
              const color = getScoreColor(loc.score);
              return (
                <div key={loc.location_id} className="flex flex-col items-center" data-testid={`podium-position-${podiumLabels[i]}`}>
                  <div className={`w-16 h-16 rounded-full ${color.bg} border-2 ${color.border} flex items-center justify-center mb-2`}>
                    <span className={`text-xl font-bold tabular-nums ${color.text}`}>{Math.round(loc.score)}</span>
                  </div>
                  <p className="text-xs font-semibold text-slate-900 dark:text-[#E6EDF3] text-center max-w-[100px] truncate">{loc.location_name}</p>
                  <p className={`text-[10px] font-medium ${color.text}`}>{getLabel(loc.score)}</p>
                  <div className={`mt-2 w-20 ${podiumHeights[i]} ${color.bg} rounded-t-lg border ${color.border} border-b-0 flex items-start justify-center pt-2`}>
                    <span className={`text-lg font-bold ${color.text}`}>#{podiumLabels[i]}</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Full Rankings Table */}
      <div className="bg-white dark:bg-[#161B22] rounded-xl border border-slate-200/80 dark:border-[#21262D] overflow-hidden">
        <div className="px-4 py-3 border-b border-slate-100 dark:border-[#21262D] flex items-center justify-between">
          <div className="flex items-center gap-2">
            <BarChart3 className="w-4 h-4 text-[#2E75B6]" />
            <span className="text-sm font-semibold text-slate-900 dark:text-[#E6EDF3] font-heading">Alle scores</span>
          </div>
          <span className="text-xs text-slate-400">{scoredLocations.length} locaties</span>
        </div>

        {scoredLocations.length === 0 ? (
          <div className="px-4 py-12 text-center">
            <ShieldCheck className="w-10 h-10 text-slate-300 mx-auto mb-3" />
            <p className="text-sm text-slate-500">Geen compliance data beschikbaar om scores te berekenen.</p>
          </div>
        ) : (
          <div>
            {/* Table header */}
            <div className="grid grid-cols-6 sm:grid-cols-12 gap-2 px-3 sm:px-4 py-2 bg-slate-50 dark:bg-[#0D1117] text-[10px] uppercase tracking-wider text-slate-500 dark:text-[#8B949E] font-medium border-b border-slate-100 dark:border-[#21262D]">
              <div className="col-span-1 hidden sm:block">#</div>
              <div className="col-span-3">Locatie</div>
              <div className="col-span-1 sm:col-span-2 text-center">Score</div>
              <div className="col-span-2 text-center hidden sm:block">Op tijd</div>
              <div className="col-span-2 text-center hidden sm:block">Snelheid</div>
              <div className="col-span-2 text-center hidden sm:block">Dekking</div>
            </div>

            {scoredLocations.map((loc, idx) => {
              const color = getScoreColor(loc.score);
              return (
                <div
                  key={loc.location_id}
                  className="grid grid-cols-6 sm:grid-cols-12 gap-2 px-3 sm:px-4 py-3 border-b border-slate-50 dark:border-[#161B22] hover:bg-slate-50/50 dark:hover:bg-[#1C2128] transition-colors"
                  data-testid={`score-row-${loc.location_id}`}
                >
                  <div className="col-span-1 text-xs font-medium text-slate-400 tabular-nums self-center hidden sm:block">{idx + 1}</div>
                  <div className="col-span-3 self-center">
                    <div className="flex items-center gap-2">
                      <span className="sm:hidden text-xs font-medium text-slate-400 tabular-nums">{idx + 1}.</span>
                      <div>
                        <p className="text-sm font-medium text-slate-900 dark:text-[#E6EDF3] truncate">{loc.location_name}</p>
                        <p className="text-[10px] text-slate-400 dark:text-[#484F58]">{loc.item_count} items</p>
                      </div>
                    </div>
                  </div>
                  <div className="col-span-2 flex items-center justify-center gap-1.5">
                    <span className={`text-sm font-bold tabular-nums ${color.text}`}>{Math.round(loc.score)}</span>
                    {loc.trend === 'up' && <TrendingUp className="w-3 h-3 text-green-500" />}
                    {loc.trend === 'down' && <TrendingDown className="w-3 h-3 text-red-500" />}
                    {loc.trend === 'stable' && <Minus className="w-3 h-3 text-slate-400" />}
                  </div>
                  <div className="col-span-2 text-center self-center hidden sm:block">
                    <MetricBar value={loc.metrics?.up_to_date} label="60%" />
                  </div>
                  <div className="col-span-2 text-center self-center hidden sm:block">
                    <MetricBar value={loc.metrics?.completion_speed} label="20%" />
                  </div>
                  <div className="col-span-2 text-center self-center hidden sm:block">
                    <MetricBar value={loc.metrics?.coverage} label="20%" />
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {noDataLocations.length > 0 && (
          <div className="px-4 py-2 border-t border-slate-100 dark:border-[#21262D]">
            <p className="text-[10px] text-slate-400 dark:text-[#484F58]">
              {noDataLocations.length} locatie(s) zonder data: {noDataLocations.map(l => l.location_name).join(', ')}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

function MetricBar({ value, label }) {
  if (value == null) return <span className="text-[10px] text-slate-300">-</span>;
  const pct = Math.round(value);
  const barColor = pct >= 80 ? 'bg-green-500' : pct >= 60 ? 'bg-blue-500' : pct >= 40 ? 'bg-orange-500' : 'bg-red-500';
  return (
    <div className="flex flex-col items-center gap-0.5">
      <span className="text-xs font-medium text-slate-700 dark:text-[#C9D1D9] tabular-nums">{pct}%</span>
      <div className="w-12 h-1.5 bg-slate-100 dark:bg-[#21262D] rounded-full overflow-hidden">
        <div className={`h-full ${barColor} rounded-full transition-all`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}
